import {
  CalendarEventDetails,
  CalendarEventGuest,
  ExternalCalendarEvent,
  ID,
} from '@griply/types';
import { isValid, parse } from 'date-fns';

import { sanitizeExternalUrl } from './sanitize-external-url';
import { parseRruleSafe } from './schedules/parse-rrule';

const DATE_KEY = /^\d{4}-\d{2}-\d{2}$/;
// Only the date and hh:mm the form parser reads; a trailing seconds/zone suffix from an older backend is tolerated.
const NAIVE_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
const DATE_KEY_FORMAT = 'yyyy-MM-dd';
const NAIVE_DATE_TIME_FORMAT = "yyyy-MM-dd'T'HH:mm";
const NAIVE_DATE_TIME_LENGTH = NAIVE_DATE_TIME_FORMAT.length - 2;
// polished throws on anything else — a trailing space or junk suffix must not slip through.
const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
// Google caps a reminder at four weeks before the event; Graph at the same order of magnitude.
const MAX_REMINDER_MINUTES = 40320;
const CANCELLED_STATUS = 'cancelled';

export enum ExternalCalendarEventContractReason {
  MissingId = 'missing-id',
  InvalidBoundary = 'invalid-boundary',
  EndBeforeStart = 'end-before-start',
  Cancelled = 'cancelled',
  TitleEmpty = 'title-empty',
  UrlInvalid = 'url-invalid',
  ColorInvalid = 'color-invalid',
  GuestsDeduped = 'guests-deduped',
  RemindersFiltered = 'reminders-filtered',
  RruleInvalid = 'rrule-invalid',
}

export type SanitizeExternalCalendarEventContext = {
  eventId?: ID;
  connectionId?: ID;
  calendarId?: ID;
  dropped: boolean;
  reasons: ExternalCalendarEventContractReason[];
};

export type SanitizeExternalCalendarEventOptions = {
  // once per event and reason set; an empty title alone is the normal shape of a private event and is not reported
  onError?: (
    error: Error,
    context: SanitizeExternalCalendarEventContext,
  ) => void;
};

export type ExternalCalendarEventSanitizer = {
  sanitizeEvent: (raw: unknown) => ExternalCalendarEvent | null;
  sanitizeEvents: (raw: unknown) => ExternalCalendarEvent[];
  sanitizeDetails: (raw: unknown) => CalendarEventDetails | null;
};

type Boundary = ExternalCalendarEvent['start'];
type RawRecord = Record<string, unknown>;
type Reasons = ExternalCalendarEventContractReason[];

const isRecord = (value: unknown): value is RawRecord =>
  !!value && typeof value === 'object';

// The regexes fix the shape; date-fns then answers whether the digits name a real day and clock time.
// 2026-02-30 passes the shape and yields an Invalid Date downstream, which throws inside a render.
const parsesTo = (value: string, format: string) =>
  isValid(parse(value, format, new Date()));

const isDateKey = (value: unknown): value is string =>
  typeof value === 'string' &&
  DATE_KEY.test(value) &&
  parsesTo(value, DATE_KEY_FORMAT);

const isNaiveDateTime = (value: unknown): value is string =>
  typeof value === 'string' &&
  NAIVE_DATE_TIME.test(value) &&
  parsesTo(value.slice(0, NAIVE_DATE_TIME_LENGTH), NAIVE_DATE_TIME_FORMAT);

const isInstant = (value: unknown): value is string =>
  typeof value === 'string' && !!value && !Number.isNaN(Date.parse(value));

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && !!value;

const isHexColor = (value: unknown): value is string =>
  typeof value === 'string' && HEX_COLOR.test(value);

const isCancelled = (raw: RawRecord): boolean =>
  raw.status === CANCELLED_STATUS || raw.isCancelled === true;

// Exactly one of an all-day date key or a parseable instant; anything else cannot be placed on a grid.
const toBoundary = (raw: unknown): Boundary | null => {
  if (!isRecord(raw)) {
    return null;
  }
  const { date, dateTime } = raw;
  if (isDateKey(date)) {
    return { date };
  }
  if (isInstant(dateTime)) {
    return { dateTime };
  }
  return null;
};

// Mixed boundary types (one a date key, the other an instant) aren't comparable — leave those alone.
const isEndBeforeStart = (start: Boundary, end: Boundary): boolean => {
  if (start.date && end.date) {
    return end.date < start.date;
  }
  if (start.dateTime && end.dateTime) {
    return Date.parse(end.dateTime) < Date.parse(start.dateTime);
  }
  return false;
};

const sanitizeUrl = (value: unknown, reasons: Reasons): string | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }
  const url = sanitizeExternalUrl(typeof value === 'string' ? value : null);
  if (!url) {
    reasons.push(ExternalCalendarEventContractReason.UrlInvalid);
  }
  return url;
};

const sanitizeTitle = (value: unknown, reasons: Reasons): string => {
  if (typeof value === 'string') {
    return value;
  }
  reasons.push(ExternalCalendarEventContractReason.TitleEmpty);
  return '';
};

const sanitizeLocation = (value: unknown): string | null =>
  isNonEmptyString(value) ? value : null;

const isGuest = (guest: unknown): guest is CalendarEventGuest =>
  isRecord(guest) && isNonEmptyString(guest.email);

// Rooms arrive without an address and the same person can be invited twice in a different letter case.
const sanitizeGuests = (
  value: unknown,
  reasons: Reasons,
): CalendarEventGuest[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  const seen = new Set<string>();
  const guests = value.filter((guest): guest is CalendarEventGuest => {
    if (!isGuest(guest)) {
      return false;
    }
    const key = guest.email.toLowerCase();
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
  if (guests.length !== value.length) {
    reasons.push(ExternalCalendarEventContractReason.GuestsDeduped);
  }
  return guests;
};

const isReminder = (
  reminder: unknown,
): reminder is CalendarEventDetails['reminders'][number] =>
  isRecord(reminder) &&
  Number.isInteger(reminder.minutes) &&
  (reminder.minutes as number) >= 0 &&
  (reminder.minutes as number) <= MAX_REMINDER_MINUTES;

const sanitizeReminders = (
  value: unknown,
  reasons: Reasons,
): CalendarEventDetails['reminders'] => {
  if (!Array.isArray(value)) {
    return [];
  }
  const reminders = value.filter(isReminder);
  if (reminders.length !== value.length) {
    reasons.push(ExternalCalendarEventContractReason.RemindersFiltered);
  }
  return reminders;
};

// null lands on the existing "pattern unknown" state instead of a throw inside the recurrence editor.
const sanitizeRrule = (value: unknown, reasons: Reasons): string | null => {
  if (value === undefined || value === null || value === '') {
    return null;
  }
  if (typeof value === 'string' && parseRruleSafe(value)) {
    return value;
  }
  reasons.push(ExternalCalendarEventContractReason.RruleInvalid);
  return null;
};

export const createExternalCalendarEventSanitizer = (
  options: SanitizeExternalCalendarEventOptions = {},
): ExternalCalendarEventSanitizer => {
  const reported = new Set<string>();

  const report = (raw: RawRecord, dropped: boolean, reasons: Reasons) => {
    const reportable = reasons.filter(
      (reason) => reason !== ExternalCalendarEventContractReason.TitleEmpty,
    );
    if (!options.onError || !reportable.length) {
      return;
    }
    const eventId = isNonEmptyString(raw.id) ? raw.id : undefined;
    const key = `${raw.connectionId ?? ''}:${eventId ?? ''}:${reportable.join(',')}`;
    if (reported.has(key)) {
      return;
    }
    reported.add(key);
    options.onError(
      new Error(
        dropped
          ? 'External calendar event dropped at the read boundary'
          : 'External calendar event coerced at the read boundary',
      ),
      {
        eventId,
        connectionId: isNonEmptyString(raw.connectionId)
          ? raw.connectionId
          : undefined,
        calendarId: isNonEmptyString(raw.calendarId)
          ? raw.calendarId
          : undefined,
        dropped,
        reasons: reportable,
      },
    );
  };

  const drop = (
    raw: RawRecord,
    reason: ExternalCalendarEventContractReason,
  ) => {
    report(raw, true, [reason]);
    return null;
  };

  // The feed read boundary: the grid sorts on `title` and places on `start`/`end`.
  const sanitizeEvent = (raw: unknown): ExternalCalendarEvent | null => {
    if (!isRecord(raw)) {
      return null;
    }
    if (!isNonEmptyString(raw.id)) {
      return drop(raw, ExternalCalendarEventContractReason.MissingId);
    }
    if (isCancelled(raw)) {
      return drop(raw, ExternalCalendarEventContractReason.Cancelled);
    }
    const start = toBoundary(raw.start);
    const end = toBoundary(raw.end);
    if (!start || !end) {
      return drop(raw, ExternalCalendarEventContractReason.InvalidBoundary);
    }
    if (isEndBeforeStart(start, end)) {
      return drop(raw, ExternalCalendarEventContractReason.EndBeforeStart);
    }
    const reasons: Reasons = [];
    const {
      color,
      url,
      connectionId,
      calendarId,
      guestCount,
      location,
      ...rest
    } = raw;
    if (color !== undefined && color !== null && !isHexColor(color)) {
      reasons.push(ExternalCalendarEventContractReason.ColorInvalid);
    }
    const sanitizedUrl = sanitizeUrl(url, reasons);
    const event: ExternalCalendarEvent = {
      ...(rest as ExternalCalendarEvent),
      id: raw.id,
      title: sanitizeTitle(raw.title, reasons),
      start,
      end,
      ...(sanitizedUrl && { url: sanitizedUrl }),
      ...(isHexColor(color) && { color }),
      ...(isNonEmptyString(connectionId) && { connectionId }),
      ...(isNonEmptyString(calendarId) && { calendarId }),
      ...(location !== undefined && { location: sanitizeLocation(location) }),
      ...(typeof guestCount === 'number' &&
        Number.isFinite(guestCount) &&
        guestCount >= 0 && { guestCount }),
    };
    report(raw, false, reasons);
    return event;
  };

  const sanitizeEvents = (raw: unknown): ExternalCalendarEvent[] => {
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw.flatMap((event) => {
      const sanitized = sanitizeEvent(event);
      return sanitized ? [sanitized] : [];
    });
  };

  // The details feed the event form; `allDay` follows the boundary shape, never the flag the backend sent.
  const sanitizeDetails = (raw: unknown): CalendarEventDetails | null => {
    if (!isRecord(raw)) {
      return null;
    }
    if (!isNonEmptyString(raw.id)) {
      return drop(raw, ExternalCalendarEventContractReason.MissingId);
    }
    if (isCancelled(raw)) {
      return drop(raw, ExternalCalendarEventContractReason.Cancelled);
    }
    const { start, end } = raw;
    const allDay = isDateKey(start);
    const isBoundary = allDay ? isDateKey : isNaiveDateTime;
    if (!isBoundary(start) || !isBoundary(end)) {
      return drop(raw, ExternalCalendarEventContractReason.InvalidBoundary);
    }
    const endBeforeStart = allDay
      ? end < start
      : Date.parse(end) < Date.parse(start);
    if (endBeforeStart) {
      return drop(raw, ExternalCalendarEventContractReason.EndBeforeStart);
    }
    const reasons: Reasons = [];
    const { organizer, videoCallProviderName, seriesRrule, ...rest } = raw;
    const details: CalendarEventDetails = {
      ...(rest as unknown as CalendarEventDetails),
      id: raw.id,
      title: sanitizeTitle(raw.title, reasons),
      allDay,
      start,
      end,
      location: sanitizeLocation(raw.location),
      guests: sanitizeGuests(raw.guests, reasons),
      reminders: sanitizeReminders(raw.reminders, reasons),
      organizer:
        isRecord(organizer) && isNonEmptyString(organizer.email)
          ? (organizer as CalendarEventDetails['organizer'])
          : null,
      videoCallUrl: sanitizeUrl(raw.videoCallUrl, reasons) ?? null,
      rrule: sanitizeRrule(raw.rrule, reasons),
      ...(seriesRrule !== undefined && {
        seriesRrule: sanitizeRrule(seriesRrule, reasons),
      }),
      ...((typeof videoCallProviderName === 'string' ||
        videoCallProviderName === null) && { videoCallProviderName }),
    };
    report(raw, false, reasons);
    return details;
  };

  return { sanitizeEvent, sanitizeEvents, sanitizeDetails };
};

const defaultSanitizer = createExternalCalendarEventSanitizer();

export const sanitizeExternalCalendarEvent = defaultSanitizer.sanitizeEvent;
export const sanitizeExternalCalendarEvents = defaultSanitizer.sanitizeEvents;
export const sanitizeCalendarEventDetails = defaultSanitizer.sanitizeDetails;
